import React from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  testId?: string;
}

export interface TabContainerProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
  className?: string;
  /** Custom class name for the content area (overrides default pt-6) */
  contentClassName?: string;
}

/**
 * TabContainer provides sticky tab navigation with scrollable content.
 *
 * Height Management:
 * - Root uses `h-full flex flex-col` to fill parent
 * - Tab nav uses `flex-shrink-0` to stay sticky at top
 * - Content uses `flex-1 overflow-auto` to enable scrolling
 *
 * Parent components must provide a height constraint (h-full or fixed height)
 * for the scrollable behavior to work correctly.
 */
export function TabContainer({
  tabs,
  activeTab,
  onTabChange,
  children,
  className,
  contentClassName,
}: TabContainerProps) {
  return (
    <div className={`h-full flex flex-col ${className || ''}`}>
      {/* Sticky Tab Navigation */}
      <div role="tablist" className="flex gap-4 border-b flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`${tab.id}-panel`}
            onClick={() => onTabChange(tab.id)}
            data-testid={tab.testId || `${tab.id}-tab`}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${
              activeTab === tab.id
                ? 'text-theme-accent border-b-2 border-theme-accent'
                : 'text-theme-text-muted hover:text-theme-text-secondary'
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scrollable Content Area */}
      <div
        role="tabpanel"
        id={`${activeTab}-panel`}
        className={`flex-1 overflow-auto ${contentClassName || 'pt-6'}`}
      >
        {children}
      </div>
    </div>
  );
}
